import Box from '@mui/material/Box';
import dynamic from 'next/dynamic';
import sectionBg from '@/styles/sectionBackgrounds.module.scss';
import {useRouter} from 'next/router';
import PageHead from '@/components/PageHead';
import {REACT_NATIVE_META_DATA} from '@/util/metaData/mobile';
import HeroVideoBanner from '@/components/Ui-components/CommonSections/HeroVideoBanner';
import {getLatestInsights} from '@/Services/latestInsights';
import {stripHtmlForJsonLd} from '@/util/seo-utils';
import {
    rnHeroImage,
    rnServicesImage,
    rnFeaturesImage,
    rnStats,
    rnServicesImageCaption,
    rnServices,
    rnFitCta,
    rnAppTypes,
    rnFeatures,
    rnIndustries,
    rnAiColumns,
    rnAiRows,
    rnProcessSteps,
    rnTechCategories,
    rnAwardCards,
    rnAwardsSubheading,
    rnIntegrations,
    rnComplianceGroups,
    rnWhyChooseCards,
    rnChallenges,
    rnCostHighlight,
    rnCostEstimator,
    rnFinalCta,
    rnFaqData
} from '@/util/contents/react-native-development-data';

const MvpEndToEndServices = dynamic(() => import('@/components/Ui-components/mvp-development/MvpEndToEndServices'));
const ClientServed = dynamic(() => import('@/components/Ui-components/home/client-served'));
const MvpStatsSection = dynamic(() => import('@/components/Ui-components/mvp-development/MvpStatsSection'));
const RnFitCta = dynamic(() => import('@/components/Ui-components/react-native-development/RnFitCta'));
const MvpTypes = dynamic(() => import('@/components/Ui-components/mvp-development/MvpTypes'));
const RnFeatureShowcase = dynamic(() => import('@/components/Ui-components/react-native-development/RnFeatureShowcase'));
const IosSuccessStories = dynamic(() => import('@/components/Ui-components/ios-development/IosSuccessStories'));
const MvpTestimonials = dynamic(() => import('@/components/Ui-components/mvp-development/MvpTestimonials'));
const HomeIndustryTabs = dynamic(() => import('@/components/Ui-components/home/HomeIndustryTabs'));
const MatrixTable = dynamic(() => import('@/components/Ui-components/CommonSections/MatrixTable'));
const MvpProcess = dynamic(() => import('@/components/Ui-components/mvp-development/MvpProcess'));
const AiStackTable = dynamic(() => import('@/components/Ui-components/ai-stack-table'));
const IosFeatureCards = dynamic(() => import('@/components/Ui-components/ios-development/IosFeatureCards'));
const ComplianceTabs = dynamic(() => import('@/components/Ui-components/CommonSections/ComplianceTabs'));
const HoverRevealCards = dynamic(() => import('@/components/Ui-components/CommonSections/HoverRevealCards'));
const CertificationsSection = dynamic(() => import('@/components/Ui-components/CertificationSection'));
const RnChallenges = dynamic(() => import('@/components/Ui-components/react-native-development/RnChallenges'));
const MvpCostTimeline = dynamic(() => import('@/components/Ui-components/mvp-development/MvpCostTimeline'));
const RnFinalCta = dynamic(() => import('@/components/Ui-components/react-native-development/RnFinalCta'));
const HomeFAQSplit = dynamic(() => import('@/components/Ui-components/home/HomeFAQSplit'));
const LatestInsights = dynamic(() => import('@/components/Ui-components/latest-insights'));

// Intrinsic size of the hero artwork (1750 × 660).
const RN_HERO_ASPECT_RATIO = 1750 / 660;

const ReactNativeAppDevelopment: any = (props: any) => {
    const {openForm, latestInsightsData, testimonials, clients, successStories} = props;
    const router = useRouter();

    return (
        <Box>
            <PageHead meta={REACT_NATIVE_META_DATA} url="/react-native-app-development" />

            <HeroVideoBanner
                heading="React Native App Development Services"
                subheading="Build scalable iOS and Android apps with React Native from a single codebase. Get a mobile product that delivers consistent experiences across platforms, integrates with your existing business systems, and is built to support future growth."
                primaryButtonText="Schedule Free Consultation"
                onPrimaryClick={openForm}
                secondaryButtonText="Explore Our Portfolio"
                onSecondaryClick={() => router.push('/portfolio')}
                backgroundImage={rnHeroImage}
                hideBackgroundImageOnMobile
                layout="split"
                splitBadgesPlacement="below-content"
                splitImageWash={false}
                splitImageAspectRatio={RN_HERO_ASPECT_RATIO}
                backgroundPosition="right center"
                splitWideContentMaxWidth="800px"
            />

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <ClientServed heading="Clients We've Served" contentData={clients} />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-deep-blue">
                <MvpStatsSection heading="Proven Expertise. Measurable Results." stats={rnStats} />
            </Box>

            <Box className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <MvpEndToEndServices
                    variant="white"
                    heading="React Native Development Services Built Around the Mobile Product Lifecycle"
                    subheading="From product planning to post-launch support, we handle the complete <a href='/blog/react-native-app-development-guide' target='_blank'>React Native development</a> lifecycle. Our teams build, integrate, test, launch, and improve applications around your business and product requirements."
                    imageSrc={rnServicesImage}
                    imageCaptionTitle={rnServicesImageCaption}
                    services={rnServices}
                />
            </Box>

            <RnFitCta {...rnFitCta} openForm={openForm} />

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpTypes
                    heading="React Native App Types for Diverse Business Requirements"
                    subheading="Our custom React Native app development approach supports different product models, user groups, and operational requirements. We build scalable <a href='/cross-platform-app-development-services' target='_blank'>cross-platform apps</a> for customer-facing platforms, enterprise workflows, SaaS products, and field operations, with support for REST API integration, third-party services, offline functionality, security controls, and device-specific capabilities."
                    eyebrow="React Native App Type"
                    calloutLabel="Key Capabilities"
                    maxVisibleTabs={7}
                    imagePosition="right"
                    imageQuality={90}
                    types={rnAppTypes}
                />
            </Box>

            <Box className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <RnFeatureShowcase
                    heading="React Native App Features We Develop for Real-World Product Requirements"
                    subheading="Our React Native app development solutions cover the features needed to build secure, reliable, and user-focused mobile applications. We develop each capability around the product's workflows, integration needs, user roles, device functions, and performance requirements."
                    items={rnFeatures}
                    image={rnFeaturesImage}
                    imageAlt="React Native mobile app showing an account balance, weekly activity chart, and recent transactions, with AI-powered and live-sync indicators"
                    imageWidth={522}
                    imageHeight={802}
                />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-deep-blue">
                <IosSuccessStories heading="React Native Case Studies" stories={successStories} onEnquire={openForm} />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpTestimonials heading="Client Testimonials" testimonials={testimonials} />
            </Box>

            <HomeIndustryTabs
                heading="React Native Solutions for Different Industries"
                subheading="We build React Native applications around the operational needs of each industry, including security, integrations, offline access, real-time data, user roles, and device capabilities. Our approach helps businesses create mobile solutions that align with existing workflows, regulatory requirements, and performance expectations across complex operating environments."
                data={rnIndustries}
                variant="overlay"
                headerAlign="center"
            />

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-deep-blue">
                <MatrixTable
                    heading="AI Integration for React Native Apps"
                    subheading="Add AI capabilities to React Native applications where they can improve user experience, reduce manual effort, and support faster business decisions."
                    criteriaLabel="AI Integration Type"
                    columns={rnAiColumns}
                    rows={rnAiRows}
                    variant="blue"
                />
            </Box>

            <Box className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <MvpProcess
                    heading="Our React Native App Development Process"
                    subheading="Our process connects product decisions with engineering execution from the beginning. Each phase produces clear technical and delivery outputs so scope, architecture, quality, and release readiness remain visible throughout the engagement."
                    steps={rnProcessSteps}
                />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <AiStackTable
                    heading="React Native Technology Stack"
                    subheading="We select technologies around product architecture, native requirements, backend systems, security, performance, testing, and release workflows. Our React Native developers use proven tools that support maintainable cross-platform products and long-term application growth."
                    contentData={rnTechCategories}
                />
            </Box>

            <Box className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <IosFeatureCards
                    heading="Integrate React Native Apps With Your Existing Business Ecosystem"
                    subheading="We integrate React Native applications with enterprise platforms, backend systems, cloud infrastructure, payments, authentication, analytics, and external services. Our approach ensures reliable data flow, consistent workflows, and secure connectivity across your mobile ecosystem."
                    cards={rnIntegrations}
                />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-deep-blue">
                <ComplianceTabs
                    heading="React Native App Security, Performance & Scalability"
                    subheading="We engineer React Native applications for enterprise environments where security, responsiveness, governance, and long-term scalability directly affect production readiness. These requirements are addressed across architecture, development, testing, deployment, and ongoing product operations."
                    groups={rnComplianceGroups}
                />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <HoverRevealCards
                    heading="Why Businesses Choose Debut Infotech for React Native Development?"
                    subheading="Selecting the right React Native app development company requires more than comparing framework expertise. Businesses need strong product engineering, native capabilities, enterprise integrations, structured QA, and reliable post-launch support. Debut Infotech brings these disciplines together under one accountable delivery model."
                    cards={rnWhyChooseCards}
                />
            </Box>

            <CertificationsSection cards={rnAwardCards} subheading={rnAwardsSubheading} />

            <Box className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <RnChallenges
                    heading="What Business Challenges Can React Native App Development Solve?"
                    subheading="Managing separate mobile platforms can increase delivery effort, slow releases, and complicate long-term maintenance. React Native helps create a shared mobile foundation while preserving native flexibility where specific platform capabilities are required."
                    challenges={rnChallenges}
                />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpCostTimeline
                    heading="How Much Does React Native App Development Cost?"
                    subheading="React Native development typically ranges from $20,000 to $150,000+, depending on the product scope and complexity."
                    highlight={rnCostHighlight}
                    estimator={rnCostEstimator}
                    ctaText="Estimate Your React Native App Cost"
                    onCtaClick={openForm}
                />
            </Box>

            <RnFinalCta {...rnFinalCta} openForm={openForm} />

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <HomeFAQSplit
                    heading="React Native App Development FAQs"
                    subheading="Answers to the questions businesses ask most often when planning, budgeting, and staffing a React Native app build."
                    faqData={rnFaqData}
                />
            </Box>

            <Box className={`latest-insights top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <LatestInsights id="18" posts={latestInsightsData} />
            </Box>

            {/* Generated from the same array the section renders, so the schema always
                matches the visible answers. */}
            <script
                type="application/ld+json"
                id="react-native-app-development-faq"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: rnFaqData
                            .flatMap((category) => category.questions)
                            .map((item) => ({
                                '@type': 'Question',
                                name: item.q,
                                acceptedAnswer: {'@type': 'Answer', text: stripHtmlForJsonLd(item.a)}
                            }))
                    })
                }}
            />
        </Box>
    );
};

// The "Latest Insights" cards (category 18) are fetched at build time so they are in the
// initial HTML and crawlable; ISR refreshes them hourly. Client logos, testimonials and case
// studies are shared with other pages and read here so those large data modules stay out of
// the client bundle.
export async function getStaticProps() {
    const [latestInsightsData, {clients}, {mvpTestimonials}, {getRnSuccessStories}] = await Promise.all([
        getLatestInsights('18'),
        import('@/util/contents/home-data'),
        import('@/util/contents/mvp-development-data'),
        import('@/util/contents/react-native-success-stories')
    ]);
    return {
        props: {latestInsightsData, clients, testimonials: mvpTestimonials, successStories: getRnSuccessStories()},
        revalidate: 3600
    };
}

export default ReactNativeAppDevelopment;
ReactNativeAppDevelopment.Layout = 'Main';
